/** Today's [00:00:00.000, 23:59:59.999] — the default date range on load, so the first fetch
 *  is scoped to a small, fast window instead of the whole ticket history. Shared by the Tickets
 *  and Analytics pages so both default the same way. */
/** Today by default; days = 30 gives the last 30 days including today. */
export const defaultDateRange = (days = 1) => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    if (days > 1) start.setDate(start.getDate() - (days - 1));
    const end = new Date(); end.setHours(23, 59, 59, 999);
    return { startDate: start, endDate: end, key: 'selection' };
};

/** The full stats and assignee reports (the Analytics page) need a date range of at most this many
 *  days; the server turns down a longer or open-ended one with ANALYTICS_RANGE_MESSAGE. The
 *  Tickets list has no such limit. */
export const ANALYTICS_MAX_RANGE_DAYS = 366;
export const ANALYTICS_RANGE_MESSAGE = `Pick a date range of up to ${ANALYTICS_MAX_RANGE_DAYS} days`;
export const isAnalyticsRangeOk = (filters) => {
    const from = Date.parse(filters?.dateFrom), to = Date.parse(filters?.dateTo);
    return !Number.isNaN(from) && !Number.isNaN(to) && from <= to && to - from <= ANALYTICS_MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;
};

// One-shot drill-down flags a stats tile or breakdown row click can set — see
// EscalationService.buildSearchCriteria (proship_mongo) for their definitions. Every tile is
// counted over the same filters as the list, date range included, so a drill-down keeps the
// date and the list shows exactly the number the tile showed.
// closedOnly is the Closed-Tickets-tab counterpart of unresolvedOnly (the Open-tab flag) — see
// the Tickets page's tab bar (mirrors V1's EscalationManagement Open/Closed tabs).
export const DRILLDOWN_FLAG_KEYS = ['unresolvedOnly', 'overdueOnly', 'dueTodayOnly', 'unassignedOnly', 'reopenedOnly',
    'assignedOnly', 'resolutionSlaMissedOnly'];
export const ONE_SHOT_DRILLDOWN_KEYS = [...DRILLDOWN_FLAG_KEYS, 'statusCategory', 'assignedToUserId', 'assignedToUserIds', 'closedOnly',
    'orderStatuses', 'ageFromDays', 'ageToDays'];
const ASSIGNEE_FILTER_KEYS = ['assignedToUserId', 'assignedToUserIds', 'unassignedOnly', 'assignedOnly'];

/** Plain names for the drill-down filters an Analytics click can apply, which the filter bar
 *  doesn't show — the Tickets page lists them so a narrowed list never looks unexplained. */
export const DRILLDOWN_LABELS = {
    unresolvedOnly: "Open or pending",
    overdueOnly: "Past TAT",
    dueTodayOnly: "Due today",
    unassignedOnly: "No assignee",
    assignedOnly: "Has an assignee",
    reopenedOnly: "Reopened at some point",
    resolutionSlaMissedOnly: "Missed resolution SLA",
    closedOnly: "Resolved or closed",
};

/** The Assignee filter's "Unassigned" option: sent as unassignedOnly when picked alone, else inside
 *  assignedToUserIds (the server's SearchEscalationRequest.UNASSIGNED_FILTER_VALUE, same string). */
export const UNASSIGNED_OPTION = '__UNASSIGNED__';

/**
 * Applies a drill-down patch on top of the filters already in effect: replaces any PREVIOUS
 * one-shot drill-down (never stacks — e.g. clicking "Due Today" then "Overdue" replaces rather
 * than ANDing both, which would nearly always be empty). Everything else, the date range
 * included, carries over. Pure function — the caller forwards the result as a query string
 * when navigating from the Analytics page to the Tickets page.
 */
export const mergeDrilldownPatch = (prevFilters, patch) => {
    const next = { ...prevFilters };
    // The assignee keys can also come from the filter bar's Assignee field — keep that choice
    // unless the drill-down is itself about assignee (Unassigned tile, Assignee breakdown row).
    const patchSetsAssignee = ASSIGNEE_FILTER_KEYS.some((key) => key in patch);
    ONE_SHOT_DRILLDOWN_KEYS
        .filter((key) => patchSetsAssignee || !ASSIGNEE_FILTER_KEYS.includes(key))
        .forEach((key) => delete next[key]);
    return { ...next, ...patch };
};

/** Encodes a filters object (dateFrom/dateTo/merchantId/overdueOnly/etc, same shape sent to
 *  /api/escalation/search) as a URL query string, for handing a drill-down off across a page
 *  navigation (Analytics -> Tickets) since the two pages don't share React state. */
export const filtersToSearchParams = (filters) => {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        // Lists as repeated params (?categories=a&categories=b): category names can hold commas.
        if (Array.isArray(value)) value.forEach((v) => params.append(key, String(v)));
        else params.set(key, String(value));
    });
    return params;
};

const BOOLEAN_FILTER_KEYS = DRILLDOWN_FLAG_KEYS;
// Sent as arrays. Links made before lists were repeated params wrote these two as "a,b".
const COMMA_LIST_KEYS = ['courierNames', 'orderStatuses'];
const LIST_FILTER_KEYS = [...COMMA_LIST_KEYS, 'merchantIds', 'categories', 'subCategories', 'statuses', 'severities', 'assignedToUserIds', 'platforms'];

/** Reverses filtersToSearchParams — reads a URLSearchParams (from useLocation().search) back
 *  into a filters object, coercing the boolean drill-down flags. */
export const searchParamsToFilters = (searchParams) => {
    const filters = {};
    for (const [key, value] of searchParams.entries()) {
        if (LIST_FILTER_KEYS.includes(key)) {
            const values = COMMA_LIST_KEYS.includes(key) ? value.split(',') : [value];
            filters[key] = [...(filters[key] || []), ...values.filter(Boolean)];
        } else filters[key] = BOOLEAN_FILTER_KEYS.includes(key) ? value === 'true' : value;
    }
    return filters;
};
